import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiMail, FiPhone, FiMapPin, FiSend, FiLoader, FiXCircle } from "react-icons/fi";

// Note: The Header component is removed as this page now has its own integrated header/hero section.
// You can add it back if it's part of a required site-wide layout.

function Contact() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    service: "",
    message: "",
    confirmPolicy: false,
    receiveUpdates: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.confirmPolicy) {
        setToast({ type: "error", message: "You must agree to the privacy policy to continue." });
        setTimeout(() => setToast(null), 5000);
        return;
    }

    setIsSubmitting(true);
    setToast(null);

    const dataToSend = {
      name: `${formData.firstName} ${formData.lastName}`,
      email: formData.email,
      service_name: formData.service,
      phone: formData.phone,
      message: formData.message,
    };
    
    try {
      // Simulating API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      // const response = await fetch("YOUR_API_ENDPOINT", { ... });
      // const result = await response.json();
      
      setToast({ type: "success", message: "Thank you for your message! Our team will connect with you soon." });
      // Clear form
      setFormData({
        firstName: "", lastName: "", email: "", phone: "",
        service: "", message: "", confirmPolicy: false, receiveUpdates: false,
      });

    } catch (error) {
      setToast({ type: "error", message: "An error occurred. Please try again later." });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setToast(null), 5000);
    }
  };

  return (
    <>
      <main className="bg-slate-50">
        {/* --- Hero Section --- */}
        <section className="bg-gray-900">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
    <motion.h1 
      className="text-4xl sm:text-6xl font-extrabold text-white tracking-tight"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7 }}
    >
      Get in Touch
    </motion.h1>
    <motion.p 
      className="mt-6 max-w-2xl mx-auto text-lg text-gray-300"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.2 }}
    >
      Our friendly team would love to hear from you! Whether you have a question about our services or want to start a project, we're here to help.
    </motion.p>
  </div>
</section>


        {/* --- Main Content: Two-Column Layout --- */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                {/* --- Left Column: Contact Info --- */}
                <motion.div 
                    className="space-y-8"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.7, delay: 0.4 }}
                >
                    <div className="bg-white p-6 rounded-xl border border-slate-200/80">
                        <h3 className="text-lg font-semibold text-slate-800 mb-4">Contact Information</h3>
                        <ul className="space-y-3 text-slate-600">
                            <li className="flex items-center gap-3"><FiMail className="text-blue-500"/> <span>info@alsoltech.in</span></li>
                            <li className="flex items-center gap-3"><FiPhone className="text-blue-500"/> <span>+91 89108 80356</span></li>
                        </ul>
                    </div>
                    <div className="bg-white p-6 rounded-xl border border-slate-200/80">
                        <h3 className="text-lg font-semibold text-slate-800 mb-4">Our Office</h3>
                        <p className="flex items-start gap-3 text-slate-600">
                            <FiMapPin className="text-blue-500 mt-1 flex-shrink-0"/> 
                            <span>Office no. 101, Matrix Tower, DN 24, Sector V, Salt Lake, Kolkata, West Bengal-700091</span>
                        </p>
                    </div>
                     <div className="bg-white p-6 rounded-xl border border-slate-200/80">
                        <h3 className="text-lg font-semibold text-slate-800 mb-4">Business Hours</h3>
                        <ul className="space-y-2 text-slate-600 text-sm">
                            <li>Monday - Friday: 9am - 6pm</li>
                            <li>Saturday: 10am - 4pm</li>
                            <li>Sunday: Closed</li>
                        </ul>
                    </div>
                </motion.div>

                {/* --- Right Column: Contact Form --- */}
                <motion.div 
                    className="lg:col-span-2 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200/80 shadow-lg"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.6 }}
                >
                    <h2 className="text-2xl font-bold text-slate-800 mb-6">Send us a Message</h2>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Section 1: Your Information */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="firstName" className="block text-sm font-medium text-slate-600 mb-1">First Name</label>
                                <input type="text" id="firstName" name="firstName" required value={formData.firstName} onChange={handleChange} className="w-full bg-slate-50 border-slate-300 p-3 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"/>
                            </div>
                            <div>
                                <label htmlFor="lastName" className="block text-sm font-medium text-slate-600 mb-1">Last Name</label>
                                <input type="text" id="lastName" name="lastName" required value={formData.lastName} onChange={handleChange} className="w-full bg-slate-50 border-slate-300 p-3 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"/>
                            </div>
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-slate-600 mb-1">Email Address</label>
                                <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full bg-slate-50 border-slate-300 p-3 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"/>
                            </div>
                            <div>
                                <label htmlFor="phone" className="block text-sm font-medium text-slate-600 mb-1">Phone Number</label>
                                <input type="tel" id="phone" name="phone" required value={formData.phone} onChange={handleChange} className="w-full bg-slate-50 border-slate-300 p-3 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"/>
                            </div>
                        </div>

                        {/* Section 2: Your Message */}
                        <div>
                            <label htmlFor="service" className="block text-sm font-medium text-slate-600 mb-1">How can we help?</label>
                            <select id="service" name="service" required value={formData.service} onChange={handleChange} className="w-full bg-slate-50 border-slate-300 p-3 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500">
                                <option value="" disabled>Select a Service</option>
                                <option>Website Design & Development</option>
                                <option>Content Writing</option>
                                <option>Digital Marketing & SEO</option>
                                <option>Android/iOS Application</option>
                                <option>Other</option>
                            </select>
                        </div>
                        <div>
                            <label htmlFor="message" className="block text-sm font-medium text-slate-600 mb-1">Message</label>
                            <textarea id="message" name="message" required value={formData.message} onChange={handleChange} rows="5" className="w-full bg-slate-50 border-slate-300 p-3 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" placeholder="Tell us about your project..."></textarea>
                        </div>

                        {/* Section 3: Agreements */}
                        <div className="space-y-4">
                            <label className="flex items-start gap-3 text-sm text-slate-600 cursor-pointer">
                                <input type="checkbox" name="confirmPolicy" checked={formData.confirmPolicy} onChange={handleChange} className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-0.5 flex-shrink-0"/>
                                <span>I confirm that I have read and agree to Alsol Technology's Privacy Policy.</span>
                            </label>
                        </div>

                        {/* Submit Button */}
                        <div className="pt-2">
                             <button type="submit" disabled={isSubmitting} className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold text-lg hover:bg-blue-700 disabled:bg-slate-400 transition-all duration-300 transform hover:scale-105">
                                {isSubmitting ? <><FiLoader className="animate-spin" /> Submitting...</> : <><FiSend /> Send Message</>}
                             </button>
                        </div>
                    </form>
                </motion.div>
            </div>
        </div>
      </main>

      {/* --- Toast Notification --- */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ ease: "easeOut", duration: 0.3 }}
            className={`fixed top-5 right-5 p-4 rounded-lg shadow-2xl z-50 flex items-start gap-3 ${
              toast.type === "success" ? "bg-green-500 text-white" : "bg-red-500 text-white"
            }`}
          >
            <span>{toast.message}</span>
            <button onClick={() => setToast(null)} className="p-1 -m-1 rounded-full hover:bg-white/20">
                <FiXCircle/>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default Contact;